import React, { useState } from 'react';
import {
  CurrencyDollar, Leaf, Users, ShieldCheck,
  TrendUp, TrendDown, Minus, ChartLineUp, Factory,
  FilePdf, MapPin, CaretRight, ArrowSquareOut, Sliders,
  Drop, Anchor, Buildings, HardHat
} from '@phosphor-icons/react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { exportCockpitPDF } from '../utils/exportPdf';
import { useTranslation } from '../i18n/LanguageContext';

const INDUSTRY_ICONS = {
  'Manufacturing': Factory,
  'Oil and Gas': Drop,
  'Marine': Anchor,
  'Built Environment': Buildings,
  'Materials and Construction': HardHat,
};

function KpiCard({ title, value, subtitle, icon: Icon, trend, trendValue, pillar, delay, onClick }) {
  const pillarColors = {
    economic: { bg: 'bg-secondary/10', text: 'text-secondary' },
    environmental: { bg: 'bg-primary/10', text: 'text-primary' },
    social: { bg: 'bg-info/10', text: 'text-info' },
    governance: { bg: 'bg-tertiary/10', text: 'text-tertiary' },
  };
  const colors = pillarColors[pillar] || pillarColors.economic;
  const TrendIcon = trend === 'up' ? TrendUp : trend === 'down' ? TrendDown : Minus;
  const trendColor = trend === 'up' ? 'text-success' : trend === 'down' ? 'text-alert-critical' : 'text-tertiary';

  return (
    <div
      data-testid={`kpi-card-${pillar}`}
      onClick={onClick}
      className={`bg-surface border border-border rounded-lg p-4 md:p-5 hover:border-tertiary/40 hover:shadow-sm transition-all duration-200 animate-fade-in ${delay} ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center`}>
          <Icon size={18} weight="duotone" className={colors.text} />
        </div>
        <div className="flex items-center gap-2">
          {trendValue && (
            <div className={`flex items-center gap-1 text-xs font-body font-medium ${trendColor}`}>
              <TrendIcon size={14} weight="bold" />
              {trendValue}
            </div>
          )}
          {onClick && <ArrowSquareOut size={14} className="text-border" />}
        </div>
      </div>
      <p className="text-xs font-body font-semibold uppercase tracking-[0.15em] text-tertiary mb-1">{title}</p>
      <p className="font-heading text-2xl font-bold text-text-primary">{value}</p>
      {subtitle && <p className="text-xs font-body text-text-secondary mt-1">{subtitle}</p>}
    </div>
  );
}

function ControlStatusPie({ data }) {
  if (!data) return null;
  const chartData = [
    { name: 'Green', value: data.green, color: '#2D6A4F' },
    { name: 'Amber', value: data.amber, color: '#F4A261' },
    { name: 'Red', value: data.red, color: '#E07A5F' },
  ].filter(d => d.value > 0);

  return (
    <div className="flex items-center gap-4">
      <div className="w-16 h-16">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={chartData} innerRadius={18} outerRadius={28} paddingAngle={3} dataKey="value" stroke="none">
              {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1">
        {chartData.map(d => (
          <div key={d.name} className="flex items-center gap-2 text-xs font-body">
            <div className="w-2 h-2 rounded-full" style={{ background: d.color }} />
            <span className="text-text-secondary">{d.name}</span>
            <span className="font-medium text-text-primary">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border rounded-lg p-3 shadow-lg text-xs font-body">
      <p className="font-medium text-text-primary mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-text-secondary">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: <span className="font-medium text-text-primary">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function ExecutiveCockpit({ kpis, summary, industry, industryContext, loading, sites, onSiteClick, onOpenThresholds }) {
  const [exporting, setExporting] = useState(false);
  const [showSites, setShowSites] = useState(false);
  const IndustryIcon = INDUSTRY_ICONS[industry] || Factory;
  const { t } = useTranslation();

  const handleExport = async () => {
    setExporting(true);
    await exportCockpitPDF(industry);
    setExporting(false);
  };

  if (loading || !kpis) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="loading-skeleton">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-surface border border-border rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-72 bg-surface border border-border rounded-lg" />
          <div className="h-72 bg-surface border border-border rounded-lg" />
        </div>
      </div>
    );
  }

  const ctx = industryContext || {};

  return (
    <div className="space-y-6" data-testid="executive-cockpit">
      {/* Section header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight">
            {t('cockpit.title')}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <IndustryIcon size={16} weight="duotone" className="text-primary" />
            <p className="font-body text-sm text-text-secondary">
              {ctx.description || t('cockpit.subtitle', { industry })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {ctx.output_value && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/10 rounded-lg">
              <span className="text-[10px] font-body font-semibold text-tertiary uppercase tracking-wider">{ctx.output_label || 'Output'}:</span>
              <span className="text-xs font-heading font-bold text-primary">{ctx.output_value}</span>
            </div>
          )}
          <div className="hidden md:flex items-center gap-2">
            <Factory size={16} className="text-tertiary" />
            <span className="text-xs font-body text-tertiary">{summary?.total_sites || 0} sites</span>
          </div>
          {onOpenThresholds && (
            <button
              data-testid="open-thresholds-button"
              onClick={onOpenThresholds}
              className="flex items-center gap-2 px-3 py-2 border border-border text-text-primary rounded-lg font-heading text-xs font-semibold hover:bg-background transition-all duration-200"
            >
              <Sliders size={16} weight="duotone" />
              <span className="hidden sm:inline">{t('cockpit.thresholds')}</span>
            </button>
          )}
          <button
            data-testid="export-pdf-button"
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-heading text-xs font-semibold hover:bg-[#2B3523] transition-all duration-200 disabled:opacity-50"
          >
            <FilePdf size={16} weight="duotone" />
            {exporting ? t('cockpit.generating') : t('cockpit.boardPack')}
          </button>
        </div>
      </div>

      {/* KPI Cards — industry-adaptive labels */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title={ctx.economic_title || "Cost per Unit"}
          value={`$${kpis.economic?.cost_per_unit || 0}`}
          subtitle={ctx.economic_subtitle || `OEE: ${kpis.economic?.oee || 0}%`}
          icon={CurrencyDollar}
          trend="down"
          trendValue="-2.3%"
          pillar="economic"
          delay="stagger-1"
          onClick={() => setShowSites(!showSites)}
        />
        <KpiCard
          title={ctx.environmental_title || "Carbon Intensity"}
          value={`${kpis.environmental?.carbon_intensity || 0}`}
          subtitle={ctx.environmental_subtitle || `Energy: ${kpis.environmental?.energy_mwh || 0} MWh`}
          icon={Leaf}
          trend="down"
          trendValue="-4.1%"
          pillar="environmental"
          delay="stagger-2"
          onClick={() => setShowSites(!showSites)}
        />
        <KpiCard
          title={ctx.social_title || "TRIR"}
          value={kpis.social?.trir || 0}
          subtitle={ctx.social_subtitle || `Training: ${kpis.social?.training_compliance || 0}%`}
          icon={Users}
          trend="down"
          trendValue="-0.08"
          pillar="social"
          delay="stagger-3"
          onClick={() => setShowSites(!showSites)}
        />
        <KpiCard
          title={ctx.governance_title || "Assurance Coverage"}
          value={`${kpis.governance?.assurance_coverage || 0}%`}
          subtitle={ctx.governance_subtitle || `Quality: ${kpis.governance?.data_quality_score || 0}`}
          icon={ShieldCheck}
          trend="up"
          trendValue="+1.2%"
          pillar="governance"
          delay="stagger-4"
          onClick={() => setShowSites(!showSites)}
        />
      </div>

      {/* Site drill-down selector */}
      {showSites && sites && sites.length > 0 && (
        <div className="bg-surface border border-border rounded-lg p-4 animate-fade-in" data-testid="site-selector-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-semibold text-text-primary">{t('cockpit.selectSite')}</h3>
            <button onClick={() => setShowSites(false)} className="text-xs font-body text-tertiary hover:text-text-primary transition-colors">{t('cockpit.close')}</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {sites.map((site) => (
              <button
                key={site.name}
                data-testid={`site-drilldown-${site.name.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => { onSiteClick(site.name); setShowSites(false); }}
                className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin size={16} weight="duotone" className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-medium text-text-primary">{site.name}</p>
                  <p className="text-[10px] font-body text-tertiary">{site.type} — {site.region}</p>
                </div>
                <CaretRight size={14} className="text-border group-hover:text-primary transition-colors" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-surface border border-border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-base font-semibold text-text-primary">{t('cockpit.energyCarbon')}</h3>
              <p className="text-xs font-body text-text-secondary">{t('cockpit.energyCarbonSub', { industry })}</p>
            </div>
            <ChartLineUp size={18} className="text-tertiary" />
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <AreaChart data={summary?.trend_data || []}>
                <defs>
                  <linearGradient id="energyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3D4B32" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3D4B32" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="co2Grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#675D3A" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#675D3A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#888864' }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: '#888864' }} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="energy" name="Energy (MWh)" stroke="#3D4B32" fill="url(#energyGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="co2" name="CO2 (tCO2e)" stroke="#675D3A" fill="url(#co2Grad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-surface border border-border rounded-lg p-5">
            <h3 className="font-heading text-base font-semibold text-text-primary mb-3">Control Status</h3>
            <ControlStatusPie data={kpis.governance?.control_status} />
          </div>
          <div className="bg-surface border border-border rounded-lg p-5">
            <h3 className="font-heading text-base font-semibold text-text-primary mb-1">{t('cockpit.ebitdaRisk')}</h3>
            <p className="font-heading text-3xl font-extrabold text-alert-critical">{kpis.economic?.ebitda_at_risk}</p>
            <p className="text-xs font-body text-text-secondary mt-1">{t('cockpit.ebitdaRiskSub', { industry })}</p>
          </div>
        </div>
      </div>

      {/* OEE & Cost bar chart */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-base font-semibold text-text-primary">
              {industry === 'Marine' ? 'Utilization & Voyage Cost' : industry === 'Built Environment' ? 'Availability & Operating Cost' : 'OEE & Cost Performance'}
            </h3>
            <p className="text-xs font-body text-text-secondary">Daily operational efficiency — {industry}</p>
          </div>
        </div>
        <div className="h-48">
          <ResponsiveContainer>
            <BarChart data={(summary?.trend_data || []).slice(-7)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#888864' }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#888864' }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="oee" name={industry === 'Marine' ? 'Utilization %' : 'OEE %'} fill="#3D4B32" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cost" name="Cost/Unit" fill="#888864" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Active Scenarios */}
      {summary?.scenarios?.length > 0 && (
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-4">
            {t('cockpit.activeScenarios', { industry })}
          </h3>
          <div className="space-y-3">
            {summary.scenarios.map((s, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border hover:border-tertiary/40 transition-all" data-testid={`scenario-card-${i}`}>
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                  s.severity === 'critical' ? 'bg-alert-critical' : s.severity === 'high' ? 'bg-alert-warning' : 'bg-info'
                }`} />
                <div className="min-w-0">
                  <p className="font-body text-sm font-medium text-text-primary">{s.title}</p>
                  <p className="font-body text-xs text-text-secondary mt-0.5">{s.trigger}</p>
                  <p className="font-body text-xs text-secondary font-medium mt-1">Impact: {s.impact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

